import { client, log } from ".";
import { getDesglosePorEdificio } from "./comisionEdificiosUtil";

/**
 * Carga los denominadores congelados del snapshot para un país, en un Map keyed por la misma
 * clave que `keyDeFactura` ('s:{sale_id}' / 'c:{contrato_id}|{mes}'). Lo consumen los motores
 * (Paso 5) para dividir por la foto en vez de `pantallastotales`. Una sola query, país-scopeada.
 * Aún NO lo usa ningún motor (andamiaje del Paso 5).
 */
export const cargarSnapshotDenominadores = async (pais) => {
  const { data, error } = await client
    .from("comision_snapshot")
    .select("origen_tipo, sale_id, contrato_id, mes, denominador_total")
    .eq("pais", pais);
  if (error) throw error;
  const map = new Map();
  for (const r of data || []) {
    const key = r.origen_tipo === "sale" ? `s:${r.sale_id}` : `c:${r.contrato_id}|${r.mes}`;
    map.set(key, r.denominador_total);
  }
  return map;
};

/**
 * Carga las FOTOS CONGELADAS del % de intermediación (A3, ver 7.22) de un país. SOLO filas con
 * `pct_congelado_en` NOT NULL. Map keyed IGUAL que `cargarSnapshotDenominadores`/`keyDeFactura`
 * ('s:{sale_id}' / 'c:{contrato_id}|{mes}'); value = `{ agencia, apx, broker, descuento }` (%).
 * `map.has(key)` (o `map.get(key)` truthy) = "esta venta/mes tiene el % congelado → usarlo tal cual".
 * Si no está en el map → resolver en vivo. Una sola query, país-scopeada (misma vida que el snapMap).
 */
export const cargarSnapshotPctCongelado = async (pais) => {
  const { data, error } = await client
    .from("comision_snapshot")
    .select(
      "origen_tipo, sale_id, contrato_id, mes, pct_agencia_congelado, pct_apx_congelado, pct_broker_congelado, pct_descuento_congelado"
    )
    .eq("pais", pais)
    .not("pct_congelado_en", "is", null);
  if (error) throw error;
  const map = new Map();
  for (const r of data || []) {
    const key = r.origen_tipo === "sale" ? `s:${r.sale_id}` : `c:${r.contrato_id}|${r.mes}`;
    map.set(key, {
      agencia: Number(r.pct_agencia_congelado) || 0,
      apx: Number(r.pct_apx_congelado) || 0,
      broker: Number(r.pct_broker_congelado) || 0,
      descuento: Number(r.pct_descuento_congelado) || 0,
    });
  }
  return map;
};

/**
 * Captura (o re-captura) la foto de comisión de una venta o mes-de-contrato.
 *
 * Congela el desglose de pantallas por edificio (4 circuitos) en las tablas
 * comision_snapshot (padre) + comision_snapshot_edificio (hijos), de modo que
 * editar el inventario de un edificio NO mueva la comisión histórica (deuda 9.19).
 *
 * Idempotente: si ya existe foto vigente para ese sale_id o (contrato_id, mes),
 * la reemplaza (borra la anterior — CASCADE borra hijos — y reescribe). Soporta
 * 'creacion', 'modificacion' y futura re-captura sin duplicar (respeta el UNIQUE parcial).
 *
 * Corre con el rol authenticated (la policy "Authenticated all" lo permite).
 * NO lee ni toca sales/sales_contratos/comisiones — solo escribe las tablas snapshot.
 *
 * @param {Object} p
 * @param {'sale'|'contrato_mes'} p.origen_tipo
 * @param {number} [p.sale_id]       requerido si origen_tipo='sale'
 * @param {number} [p.contrato_id]   requerido si origen_tipo='contrato_mes'
 * @param {string} [p.mes]           "MM-YYYY", requerido si origen_tipo='contrato_mes'
 * @param {Array}  p.edificios       objetos edificio participantes (con pantallas/totems/espera/cowork)
 * @param {Object} p.csvs            { pantallas_ascensores, pantallas_circulacion, pantallas_espera, pantallas_cowork }
 * @param {number} [p.monto]         importe congelado de la venta/mes
 * @param {string} p.pais
 * @param {'creacion'|'modificacion'|'backfill'} p.motivo
 */
export const capturarSnapshotComision = async ({
  origen_tipo,
  sale_id = null,
  contrato_id = null,
  mes = null,
  edificios = [],
  csvs = {},
  monto = null,
  pais,
  motivo,
}) => {
  try {
    // 1. Desglose por circuito de cada edificio participante (hijos)
    const hijos = (edificios || []).map((e) => ({
      edificio_id: e.id,
      ...getDesglosePorEdificio(e, csvs),
    }));

    // 2. Invariante: el denominador del padre es la suma de los 4 circuitos de los hijos
    const denominador_total = hijos.reduce(
      (acc, h) => acc + h.pantallas_asc + h.pantallas_circ + h.pantallas_esp + h.pantallas_cow,
      0
    );

    // 3a. Carry-forward del freeze (A4, ver 7.22): si la foto vigente estaba CONGELADA
    //     (pct_congelado_en NOT NULL), preservar las 6 columnas pct_* a través del borrar+reinsertar
    //     — el % congelado es INTOCABLE aunque se edite importe/pantallas. Se lee ANTES del borrado;
    //     por venta (sale_id) / (contrato_id, mes), sin tocar meses hermanos. Si NO estaba congelada
    //     → carryPct vacío → recaptura normal (foto sin %), sin foto espuria.
    let selPrev = client
      .from("comision_snapshot")
      .select(
        "pct_agencia_congelado, pct_apx_congelado, pct_broker_congelado, pct_descuento_congelado, pct_congelado_en, pct_congelado_motivo"
      )
      .eq("origen_tipo", origen_tipo);
    selPrev =
      origen_tipo === "sale" ? selPrev.eq("sale_id", sale_id) : selPrev.eq("contrato_id", contrato_id).eq("mes", mes);
    const { data: prevRows } = await selPrev;
    const prevPct = prevRows?.[0];
    const carryPct =
      prevPct?.pct_congelado_en != null
        ? {
            pct_agencia_congelado: prevPct.pct_agencia_congelado,
            pct_apx_congelado: prevPct.pct_apx_congelado,
            pct_broker_congelado: prevPct.pct_broker_congelado,
            pct_descuento_congelado: prevPct.pct_descuento_congelado,
            pct_congelado_en: prevPct.pct_congelado_en,
            pct_congelado_motivo: prevPct.pct_congelado_motivo,
          }
        : {};

    // 3b. Idempotencia: borrar la foto vigente de esta clave (CASCADE borra los hijos)
    let delQ = client.from("comision_snapshot").delete().eq("origen_tipo", origen_tipo);
    delQ = origen_tipo === "sale" ? delQ.eq("sale_id", sale_id) : delQ.eq("contrato_id", contrato_id).eq("mes", mes);
    const { error: delErr } = await delQ;
    if (delErr) throw delErr;

    // 4. Insertar el padre y obtener su id (+ carry-forward del freeze si aplicaba)
    const { data: padre, error: padreErr } = await client
      .from("comision_snapshot")
      .insert({
        origen_tipo,
        sale_id,
        contrato_id,
        mes,
        denominador_total,
        monto_congelado: monto,
        pais,
        motivo_captura: motivo,
        ...carryPct,
      })
      .select("id")
      .single();
    if (padreErr) throw padreErr;

    // 5. Insertar los hijos
    if (hijos.length > 0) {
      const { error: hijosErr } = await client
        .from("comision_snapshot_edificio")
        .insert(hijos.map((h) => ({ snapshot_id: padre.id, ...h })));
      if (hijosErr) throw hijosErr;
    }

    return { snapshot_id: padre.id, denominador_total, hijos: hijos.length };
  } catch (error) {
    log.error("capturarSnapshotComision", error);
    throw error;
  }
};

// Trae los edificios vivos (sus 4 circuitos) por lista de IDs.
const fetchEdificiosPorIds = async (ids) => {
  if (ids.length === 0) return [];
  const { data, error } = await client.from("edificios").select("id, pantallas, totems, espera, cowork").in("id", ids);
  if (error) throw error;
  return data || [];
};

const parseIds = (csv) =>
  (csv || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * Orquesta la captura del snapshot a partir de una fila de `sales` recién creada/editada.
 * Toda venta con edificios participantes nace con foto. Sin CSV de circuitos (carga manual
 * Perú) → `getDesglosePorEdificio` usa la rama `camposVacios` (4 circuitos del edificio),
 * mismo criterio que el backfill (CABO 1). Solo se saltea si no hay edificios participantes.
 */
export const capturarSnapshotDesdeVenta = async (venta, motivo = "creacion") => {
  const csvs = {
    pantallas_ascensores: venta.pantallas_ascensores,
    pantallas_circulacion: venta.pantallas_circulacion,
    pantallas_espera: venta.pantallas_espera,
    pantallas_cowork: venta.pantallas_cowork,
  };

  const ids = parseIds(venta.edificiosparticipantes);
  if (ids.length === 0) return null;
  const edificios = await fetchEdificiosPorIds(ids);

  return capturarSnapshotComision({
    origen_tipo: "sale",
    sale_id: venta.id,
    edificios,
    csvs,
    monto: venta.importe,
    pais: venta.pais,
    motivo,
  });
};

/**
 * Orquesta la captura de un contrato: N fotos, UNA por mes, cada una con los edificios
 * y los 4 CSV de ESE mes (no los del contrato entero). Keyed por (contrato_id, mes).
 */
export const capturarSnapshotDesdeContrato = async (contrato, motivo = "creacion") => {
  const meses = contrato.meses || [];
  const allIds = [...new Set(meses.flatMap((m) => parseIds(m.edificios_ids)))];
  if (allIds.length === 0) return [];

  const edifList = await fetchEdificiosPorIds(allIds);
  const edifMap = Object.fromEntries(edifList.map((e) => [String(e.id), e]));

  const results = [];
  for (const m of meses) {
    const ids = parseIds(m.edificios_ids);
    if (ids.length === 0) continue;
    const edificios = ids.map((id) => edifMap[id]).filter(Boolean);
    const csvs = {
      pantallas_ascensores: m.pantallas_ascensores,
      pantallas_circulacion: m.pantallas_circulacion,
      pantallas_espera: m.pantallas_espera,
      pantallas_cowork: m.pantallas_cowork,
    };
    // eslint-disable-next-line no-await-in-loop
    const r = await capturarSnapshotComision({
      origen_tipo: "contrato_mes",
      contrato_id: contrato.id,
      mes: m.mes,
      edificios,
      csvs,
      monto: m.importe,
      pais: contrato.pais,
      motivo,
    });
    results.push(r);
  }
  return results;
};

/**
 * Borra las fotos de meses que ya NO existen en el contrato (CABO 2: evita huérfanas).
 * Al eliminar un mes de un `sales_contratos`, su foto `contrato_mes` quedaría colgada
 * (la re-captura solo toca meses presentes, no borra los ausentes). CASCADE borra los hijos.
 * Aditivo: el caller la envuelve en try/catch para no abortar el update.
 */
export const eliminarSnapshotsContratoMeses = async (contrato_id, meses) => {
  if (!contrato_id || !meses || meses.length === 0) return;
  const { error } = await client
    .from("comision_snapshot")
    .delete()
    .eq("origen_tipo", "contrato_mes")
    .eq("contrato_id", contrato_id)
    .in("mes", meses);
  if (error) throw error;
};

/**
 * Invalida (limpia) la foto del % congelado de un padre: setea las 6 columnas pct_* a NULL (rama
 * all-null de chk_pct_congelado_coherencia). Se llama al DESfacturar (A4, ver 7.22): así una
 * re-facturación posterior vuelve a congelar al % vigente de ese momento (congelarPctIntermediacion
 * es idempotente y verá pct_congelado_en NULL → re-congela). Aditivo, no lanza. Por venta (sale_id)
 * o (contrato_id, mes) — no toca meses hermanos. Si ya estaba en NULL → UPDATE inocuo.
 */
export const invalidarPctCongelado = async ({ origenTipo, saleId = null, contratoId = null, mes = null }) => {
  try {
    let q = client.from("comision_snapshot").update({
      pct_agencia_congelado: null,
      pct_apx_congelado: null,
      pct_broker_congelado: null,
      pct_descuento_congelado: null,
      pct_congelado_en: null,
      pct_congelado_motivo: null,
    });
    q =
      origenTipo === "sale"
        ? q.eq("origen_tipo", "sale").eq("sale_id", saleId)
        : q.eq("origen_tipo", "contrato_mes").eq("contrato_id", contratoId).eq("mes", mes);
    const { error } = await q;
    if (error) throw error;
  } catch (e) {
    log.error("invalidarPctCongelado", e);
  }
};

// ── Flag de sensibilidad: qué cambios de una venta/mes obligan a re-capturar la foto ──
// SOLO estos campos mueven la proporción o el monto del snapshot.
export const CAMPOS_SENSIBLES_VENTA = [
  "edificiosparticipantes",
  "pantallas_ascensores",
  "pantallas_circulacion",
  "pantallas_espera",
  "pantallas_cowork",
  "pantallastotales",
  "importe",
];

// Equivalente a nivel de cada mes de un contrato.
export const CAMPOS_SENSIBLES_MES = [
  "edificios_ids",
  "pantallas_ascensores",
  "pantallas_circulacion",
  "pantallas_espera",
  "pantallas_cowork",
  "pantallas_totales",
  "importe",
];

/**
 * true SOLO si cambió algún campo sensible (proporción/monto) entre antes y después.
 * Inocuos (NO disparan): facturanumero/facturafecha/facturafechacobro/plazocobro/
 * fecha_cobro_estimada/otros_costos/eliminada/intermediario_override_id/estatus/etc.
 * Comparación estricta: nunca omite un cambio real (puede re-capturar de más ante
 * diferencias de tipo, que es el lado seguro — la captura es idempotente).
 */
export const cambioRequiereRecaptura = (antes, despues, campos = CAMPOS_SENSIBLES_VENTA) =>
  !!antes && !!despues && campos.some((c) => (antes[c] ?? null) !== (despues[c] ?? null));
